/**
 * Viewport management utilities for visible time range, pan/zoom operations, and auto-scroll.
 * The viewport determines which portion of the data is currently visible.
 */

import type { Viewport } from "~types/types";

// ==================== Types ====================

export interface ViewportConstraints {
  minRange: number;
  maxRange: number;
  dataStart: number;
  dataEnd: number;
  allowOverscroll?: boolean;
  overscrollRatio?: number;
}

export interface ViewportState {
  viewport: Viewport;
  constraints: ViewportConstraints;
  isAtRightEdge: boolean;
  isAtLeftEdge: boolean;
  zoomLevel: number;
}

type PanEdge = "left" | "right" | null;

export interface PanResult {
  viewport: Viewport;
  wasConstrained: boolean;
  reachedEdge: PanEdge;
}

type ZoomLimit = "min" | "max" | null;

export interface ZoomResult {
  viewport: Viewport;
  wasConstrained: boolean;
  reachedLimit: ZoomLimit;
}

export interface AutoScrollConfig {
  enabled: boolean;
  edgeThreshold: number;
  animationDuration: number;
}

// ==================== Viewport Creation ====================

/**
 * Create initial viewport showing all data or specified range.
 */
export function createViewport(
  dataStart: number,
  dataEnd: number,
  initialRange?: { start: number; end: number }
): Viewport {
  if (initialRange) {
    return {
      start: initialRange.start,
      end: initialRange.end,
    };
  }

  return {
    start: dataStart,
    end: dataEnd,
  };
}

/**
 * Create viewport constraints from configuration.
 */
export function createViewportConstraints(
  dataStart: number,
  dataEnd: number,
  config?: {
    minZoomRange?: number;
    maxZoomRange?: number;
    allowOverscroll?: boolean;
    overscrollRatio?: number;
  }
): ViewportConstraints {
  const dataRange = dataEnd - dataStart;

  return {
    minRange: config?.minZoomRange ?? dataRange * 0.01,
    maxRange: config?.maxZoomRange ?? dataRange * 10,
    dataStart,
    dataEnd,
    allowOverscroll: config?.allowOverscroll ?? false,
    overscrollRatio: config?.overscrollRatio ?? 0.1,
  };
}

// ==================== Pan Operations ====================

/**
 * Pan viewport by pixel delta.
 */
export function panByPixels(
  viewport: Viewport,
  deltaX: number,
  pixelsPerMs: number,
  constraints: ViewportConstraints
): PanResult {
  const deltaMs = -deltaX / pixelsPerMs;

  return panByTime(viewport, deltaMs, constraints);
}

/**
 * Pan viewport by time delta.
 */
export function panByTime(
  viewport: Viewport,
  deltaMs: number,
  constraints: ViewportConstraints
): PanResult {
  let newStart = viewport.start + deltaMs;
  let newEnd = viewport.end + deltaMs;
  let wasConstrained = false;
  let reachedEdge: "left" | "right" | null = null;

  const {
    dataStart,
    dataEnd,
    allowOverscroll,
    overscrollRatio = 0.1,
  } = constraints;
  const visibleRange = newEnd - newStart;
  const overscroll = allowOverscroll ? visibleRange * overscrollRatio : 0;

  if (newStart < dataStart - overscroll) {
    const adjustment = dataStart - overscroll - newStart;

    newStart += adjustment;
    newEnd += adjustment;
    wasConstrained = true;
    reachedEdge = "left";
  }

  if (newEnd > dataEnd + overscroll) {
    const adjustment = newEnd - (dataEnd + overscroll);

    newStart -= adjustment;
    newEnd -= adjustment;
    wasConstrained = true;
    reachedEdge = "right";
  }

  return {
    viewport: { start: newStart, end: newEnd },
    wasConstrained,
    reachedEdge,
  };
}

/**
 * Pan to specific time position (center viewport on timestamp).
 */
export function panToTimestamp(
  viewport: Viewport,
  timestamp: number,
  constraints: ViewportConstraints
): PanResult {
  const currentCenter = (viewport.start + viewport.end) / 2;
  const deltaMs = timestamp - currentCenter;

  return panByTime(viewport, deltaMs, constraints);
}

// ==================== Zoom Operations ====================

/**
 * Zoom by factor around anchor point.
 * factor > 1 = zoom in, factor < 1 = zoom out
 */
export function zoomByFactor(
  viewport: Viewport,
  factor: number,
  anchorTimestamp: number,
  constraints: ViewportConstraints
): ZoomResult {
  const currentRange = viewport.end - viewport.start;
  let newRange = currentRange / factor;
  let wasConstrained = false;
  let reachedLimit: "min" | "max" | null = null;

  if (newRange < constraints.minRange) {
    newRange = constraints.minRange;
    wasConstrained = true;
    reachedLimit = "min";
  }
  if (newRange > constraints.maxRange) {
    newRange = constraints.maxRange;
    wasConstrained = true;
    reachedLimit = "max";
  }

  const anchorRatio = (anchorTimestamp - viewport.start) / currentRange;

  const newStart = anchorTimestamp - newRange * anchorRatio;
  const newEnd = newStart + newRange;

  const clamped = clampViewport({ start: newStart, end: newEnd }, constraints);

  return {
    viewport: clamped,
    wasConstrained,
    reachedLimit,
  };
}

/**
 * Zoom to show specific time range.
 */
export function zoomToRange(
  _viewport: Viewport,
  range: { start: number; end: number },
  constraints: ViewportConstraints
): ZoomResult {
  const newRange = range.end - range.start;
  let wasConstrained = false;
  let reachedLimit: "min" | "max" | null = null;

  if (newRange < constraints.minRange) {
    wasConstrained = true;
    reachedLimit = "min";
  }
  if (newRange > constraints.maxRange) {
    wasConstrained = true;
    reachedLimit = "max";
  }

  const clamped = clampViewport(
    { start: range.start, end: range.end },
    constraints
  );

  return {
    viewport: clamped,
    wasConstrained,
    reachedLimit,
  };
}

/**
 * Zoom to fit all data.
 */
export function zoomToFit(
  viewport: Viewport,
  constraints: ViewportConstraints
): ZoomResult {
  return zoomToRange(
    viewport,
    { start: constraints.dataStart, end: constraints.dataEnd },
    constraints
  );
}

/**
 * Calculate zoom factor from wheel delta.
 */
export function wheelDeltaToZoomFactor(
  deltaY: number,
  sensitivity = 0.001
): number {
  const factor = 1 + deltaY * sensitivity;

  return Math.max(0.1, Math.min(10, factor));
}

// ==================== Auto-Scroll ====================

/**
 * Check if viewport should auto-scroll to show new data.
 */
export function shouldAutoScroll(
  viewport: Viewport,
  latestTimestamp: number,
  config: AutoScrollConfig
): boolean {
  if (!config.enabled) return false;

  return isAtRightEdge(viewport, latestTimestamp, config.edgeThreshold);
}

/**
 * Calculate new viewport for auto-scroll (shifts viewport to follow latest data).
 */
export function calculateAutoScrollViewport(
  viewport: Viewport,
  latestTimestamp: number,
  constraints: ViewportConstraints
): Viewport {
  const visibleRange = viewport.end - viewport.start;
  const newEnd = latestTimestamp;
  const newStart = newEnd - visibleRange;

  return clampViewport({ start: newStart, end: newEnd }, constraints);
}

/**
 * Calculate new viewport for auto-grow (extends viewport end while keeping start fixed).
 * Maintains a trailing buffer after the latest data point.
 * @param viewport Current viewport
 * @param latestTimestamp Latest data point timestamp
 * @param bufferRatio Trailing buffer as ratio of current visible range (default: 0.1 = 10%)
 * @param constraints Viewport constraints
 */
export function calculateAutoGrowViewport(
  viewport: Viewport,
  latestTimestamp: number,
  bufferRatio: number,
  constraints: ViewportConstraints
): Viewport {
  const currentRange = viewport.end - viewport.start;
  const buffer = currentRange * bufferRatio;
  const newEnd = latestTimestamp + buffer;

  // Keep start fixed, extend end
  return clampViewport({ start: viewport.start, end: newEnd }, constraints);
}

/**
 * Check if viewport is at right edge (showing latest data).
 */
export function isAtRightEdge(
  viewport: Viewport,
  latestTimestamp: number,
  threshold = 1000
): boolean {
  return viewport.end >= latestTimestamp - threshold;
}

// ==================== Viewport Utilities ====================

/**
 * Clamp viewport to constraints.
 */
export function clampViewport(
  viewport: Viewport,
  constraints: ViewportConstraints
): Viewport {
  const {
    dataStart,
    dataEnd,
    allowOverscroll,
    overscrollRatio = 0.1,
  } = constraints;
  const visibleRange = viewport.end - viewport.start;
  const overscroll = allowOverscroll ? visibleRange * overscrollRatio : 0;

  let { start, end } = viewport;

  const minStart = dataStart - overscroll;
  const maxEnd = dataEnd + overscroll;

  if (start < minStart) {
    start = minStart;
    end = start + visibleRange;
  }

  if (end > maxEnd) {
    end = maxEnd;
    start = end - visibleRange;
  }

  if (start < minStart) {
    start = minStart;
  }

  return { start, end };
}

/**
 * Get visible time range duration.
 */
export function getVisibleRange(viewport: Viewport): number {
  return viewport.end - viewport.start;
}

/**
 * Check if timestamp is within viewport.
 */
export function isTimestampVisible(
  viewport: Viewport,
  timestamp: number
): boolean {
  return timestamp >= viewport.start && timestamp <= viewport.end;
}

/**
 * Calculate viewport center timestamp.
 */
export function getViewportCenter(viewport: Viewport): number {
  return (viewport.start + viewport.end) / 2;
}

/**
 * Expand viewport by ratio on each side.
 */
export function expandViewport(
  viewport: Viewport,
  ratio: number,
  constraints: ViewportConstraints
): Viewport {
  const currentRange = viewport.end - viewport.start;
  const expansion = currentRange * ratio;

  const newStart = viewport.start - expansion;
  const newEnd = viewport.end + expansion;

  return clampViewport({ start: newStart, end: newEnd }, constraints);
}

// ==================== Data Point Snapping ====================

/**
 * Find the nearest timestamp that is less than or equal to target.
 */
function findNearestTimestampBefore(
  timestamps: number[],
  target: number
): number | null {
  if (timestamps.length === 0) return null;

  // Binary search for the largest timestamp <= target
  let left = 0;
  let right = timestamps.length - 1;
  let result: number | null = null;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midValue = timestamps[mid];

    if (midValue !== undefined && midValue <= target) {
      result = midValue;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return result;
}

/**
 * Find the nearest timestamp that is greater than or equal to target.
 */
function findNearestTimestampAfter(
  timestamps: number[],
  target: number
): number | null {
  if (timestamps.length === 0) return null;

  // Binary search for the smallest timestamp >= target
  let left = 0;
  let right = timestamps.length - 1;
  let result: number | null = null;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midValue = timestamps[mid];

    if (midValue !== undefined && midValue >= target) {
      result = midValue;
      right = mid - 1;
    } else {
      left = mid + 1;
    }
  }

  return result;
}

/**
 * Snap viewport boundaries to the nearest INNER data points.
 * This ensures viewport start/end are actual data point timestamps that are visible.
 *
 * Snaps INWARD:
 * - Start: finds the first data point >= viewport.start (first visible point)
 * - End: finds the last data point <= viewport.end (last visible point)
 *
 * @param viewport The viewport to snap
 * @param timestamps Sorted array of data point timestamps
 * @param constraints Viewport constraints for clamping
 * @returns Snapped viewport with boundaries at actual data points
 */
function resolveInvertedSnap(
  timestamps: number[],
  viewportStart: number,
  viewportEnd: number
): { snappedStart: number; snappedEnd: number } {
  // When snappedStart > snappedEnd, the viewport falls between data points.
  // Pick the single closest timestamp and use it for both boundaries.
  const midpoint = (viewportStart + viewportEnd) / 2;
  const closestBefore = findNearestTimestampBefore(timestamps, midpoint);
  const closestAfter = findNearestTimestampAfter(timestamps, midpoint);

  if (closestBefore !== null && closestAfter !== null) {
    const pick =
      midpoint - closestBefore <= closestAfter - midpoint
        ? closestBefore
        : closestAfter;

    return { snappedStart: pick, snappedEnd: pick };
  }

  if (closestBefore !== null) {
    return { snappedStart: closestBefore, snappedEnd: closestBefore };
  }

  if (closestAfter !== null) {
    return { snappedStart: closestAfter, snappedEnd: closestAfter };
  }

  return { snappedStart: viewportStart, snappedEnd: viewportEnd };
}

function tryExpandEnd(
  timestamps: number[],
  endIdx: number
): { value: number; idx: number } | null {
  if (endIdx >= timestamps.length - 1) return null;

  const nextEnd = timestamps[endIdx + 1];

  return nextEnd === undefined ? null : { value: nextEnd, idx: endIdx + 1 };
}

function tryExpandStart(
  timestamps: number[],
  startIdx: number
): { value: number; idx: number } | null {
  if (startIdx <= 0) return null;

  const prevStart = timestamps[startIdx - 1];

  return prevStart === undefined
    ? null
    : { value: prevStart, idx: startIdx - 1 };
}

function expandToMinRange(
  timestamps: number[],
  snappedStart: number,
  snappedEnd: number,
  minRange: number
): { start: number; end: number } {
  let expandedStart = snappedStart;
  let expandedEnd = snappedEnd;
  let startIdx = timestamps.indexOf(expandedStart);
  let endIdx = timestamps.indexOf(expandedEnd);

  while (
    expandedEnd - expandedStart < minRange &&
    (startIdx > 0 || endIdx < timestamps.length - 1)
  ) {
    const prevStartIdx = startIdx;
    const prevEndIdx = endIdx;

    const endExpansion = tryExpandEnd(timestamps, endIdx);

    if (endExpansion) {
      expandedEnd = endExpansion.value;
      endIdx = endExpansion.idx;
    }

    if (expandedEnd - expandedStart < minRange) {
      const startExpansion = tryExpandStart(timestamps, startIdx);

      if (startExpansion) {
        expandedStart = startExpansion.value;
        startIdx = startExpansion.idx;
      }
    }

    if (startIdx === prevStartIdx && endIdx === prevEndIdx) break;
  }

  return { start: expandedStart, end: expandedEnd };
}

export function snapViewportToData(
  viewport: Viewport,
  timestamps: number[],
  constraints: ViewportConstraints
): Viewport {
  const firstTimestamp = timestamps[0];
  const lastTimestamp = timestamps.at(-1);

  if (firstTimestamp === undefined || lastTimestamp === undefined) {
    return viewport;
  }

  // Snap inward: start = first data point >= viewport.start, end = last data point <= viewport.end
  let snappedStart =
    findNearestTimestampAfter(timestamps, viewport.start) ?? lastTimestamp;
  let snappedEnd =
    findNearestTimestampBefore(timestamps, viewport.end) ?? firstTimestamp;

  // If inverted, the viewport falls entirely between two data points
  if (snappedStart > snappedEnd) {
    const resolved = resolveInvertedSnap(
      timestamps,
      viewport.start,
      viewport.end
    );

    snappedStart = resolved.snappedStart;
    snappedEnd = resolved.snappedEnd;
  }

  if (snappedEnd - snappedStart < constraints.minRange) {
    const expanded = expandToMinRange(
      timestamps,
      snappedStart,
      snappedEnd,
      constraints.minRange
    );

    snappedStart = expanded.start;
    snappedEnd = expanded.end;
  }

  return clampViewport({ start: snappedStart, end: snappedEnd }, constraints);
}
